/** Course detail page — hero summary + scalable module/topic/lesson curriculum accordion. */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { Play, RotateCcw } from 'lucide-react';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  useCourseWithContent,
  useCourseEnrolment,
  useCourseProgress,
  useCourseTimeEstimates,
  useCreateReviseSession,
} from '@/shared/api/hooks';
import type { CourseProgress } from '@formency/shared';
import { Button, PageContainer, ProgressRing } from '@/interfaces/learner/ui';
import { formatEstimatedTime } from '@/shared/utils/formatEstimatedTime';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';
import CourseCurriculumAccordion from './CourseCurriculumAccordion';
import CourseActionPanel from './CourseActionPanel';
import CourseHeroHeader from './CourseHeroHeader';
import PreSubscriptionPage from './PreSubscriptionPage';
import PrivateCoursePage from './PrivateCoursePage';
import {
  countPublishedLessons,
  countPublishedTopics,
  filterPublishedModules,
} from './courseContent';

const EASE = [0.16, 1, 0.3, 1] as const;

export default function CoursePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { guardSession } = useSessionGuard();
  const {
    data: course,
    isLoading: loadingCourse,
    error: courseError,
  } = useCourseWithContent(courseId);
  const { data: enrolment, isLoading: loadingEnrolment } = useCourseEnrolment(courseId);
  const {
    data: progress,
    isLoading: loadingProgress,
    refetch: refetchProgress,
  } = useCourseProgress(courseId);
  const { data: timeEstimates } = useCourseTimeEstimates(courseId);
  const createReviseSession = useCreateReviseSession();

  const modules = useMemo(() => filterPublishedModules(course?.modules ?? []), [course?.modules]);

  const lessonLocations = useMemo(() => {
    const map = new Map<string, LessonLocation>();
    let order = 0;

    for (const module of modules) {
      for (const topic of module.topics ?? []) {
        for (const lesson of topic.lessons ?? []) {
          order += 1;
          map.set(lesson.id, { moduleId: module.id, topicId: topic.id, order });
        }
      }
    }

    return map;
  }, [modules]);

  const totalLessons = progress?.totalLessons ?? countPublishedLessons(modules);
  const completedLessons = progress?.completedLessons ?? 0;
  const percentage =
    progress?.percentage ??
    (totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0);
  const nextLesson = useMemo(() => findNextLesson(progress), [progress]);
  const activeLessonLocation = nextLesson ? lessonLocations.get(nextLesson.lessonId) : undefined;
  const activeModuleId = activeLessonLocation?.moduleId ?? modules[0]?.id;
  const activeTopicId = activeLessonLocation?.topicId;

  const [selectionInitialized, setSelectionInitialized] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  useEffect(() => {
    if (selectionInitialized || modules.length === 0) return;
    setSelectedModuleId(activeModuleId ?? null);
    setSelectionInitialized(true);
  }, [selectionInitialized, modules, activeModuleId]);

  useEffect(() => {
    if (!selectedModuleId) return;
    const exists = modules.some((module) => module.id === selectedModuleId);
    if (!exists) setSelectedModuleId(activeModuleId ?? null);
  }, [selectedModuleId, modules, activeModuleId]);

  const timeRemaining = useMemo(() => {
    if (!timeEstimates?.hasPersonalData || timeEstimates.estimatedMs == null) return null;
    if (percentage >= 100) return null;
    const remainingMs = timeEstimates.estimatedMs * (1 - percentage / 100);
    return formatEstimatedTime(remainingMs);
  }, [timeEstimates, percentage]);

  const nextLessonOrder = nextLesson ? lessonLocations.get(nextLesson.lessonId)?.order : undefined;
  const courseDescription = course?.description?.trim();
  const progressSummary = buildHeroSubtitle({
    totalLessons,
    completedLessons,
    percentage,
    nextLessonOrder,
    timeRemaining,
  });
  const moduleCount = modules.length;
  const topicCount = countPublishedTopics(modules);

  const [continueLoading, setContinueLoading] = useState(false);

  const handleOpenLesson = useCallback(
    (lessonId: string) => {
      if (!courseId) return;
      guardSession({
        lessonId,
        onProceed: () => navigate(`/courses/${courseId}/${lessonId}`),
      });
    },
    [guardSession, navigate, courseId]
  );

  const handleContinue = useCallback(async () => {
    if (!courseId) return;
    setContinueLoading(true);
    try {
      const { data: freshProgress } = await refetchProgress();
      const target = findNextLesson(freshProgress ?? progress);
      if (!target) {
        toast.error('No lessons available to continue');
        return;
      }

      guardSession({
        lessonId: target.lessonId,
        onProceed: () => navigate(`/courses/${courseId}/${target.lessonId}`),
      });
    } catch {
      toast.error('Failed to load course progress');
    } finally {
      setContinueLoading(false);
    }
  }, [courseId, refetchProgress, progress, guardSession, navigate]);

  const completedLessonIds = useMemo(
    () =>
      (progress?.lessons ?? [])
        .filter((lesson) => lesson.status === 'completed')
        .map((lesson) => lesson.lessonId),
    [progress]
  );

  const handleRevise = useCallback(() => {
    if (completedLessonIds.length === 0) return;

    guardSession({
      onProceed: () =>
        createReviseSession.mutate(
          { lessonIds: completedLessonIds, maxQuestions: 20 },
          {
            onSuccess: () => navigate('/revision/session', { state: { newSession: true } }),
            onError: (error) => toast.error(error.message),
          }
        ),
    });
  }, [completedLessonIds, guardSession, createReviseSession, navigate]);

  // Private course — show lock page (check first, before loading/enrollment guards)
  const isPrivateCourse =
    (courseError as AxiosError<{ reason?: string }>)?.response?.data?.reason === 'private_course';
  if (isPrivateCourse) {
    return <PrivateCoursePage />;
  }

  // Not enrolled → show pre-subscription page
  if (!loadingEnrolment && !enrolment && course) {
    return <PreSubscriptionPage course={course} />;
  }

  if (loadingCourse || loadingEnrolment || loadingProgress) {
    return <CoursePageSkeleton />;
  }

  if (!course) {
    return (
      <PageContainer className="pt-14 text-center">
        <p className="text-meta-muted">Course not found.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="relative pb-24">
      <section className="relative" style={{ paddingTop: 'var(--page-top-spacing)' }}>
        <div className="grid gap-8 learner-double:grid-cols-[minmax(0,1fr)_minmax(380px,var(--learner-context-rail-width))]">
          <div className="min-w-0">
            <motion.div
              className="mt-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.05, ease: EASE }}
            >
              <CourseHeroHeader
                eyebrow={`Course · ${totalLessons} lessons`}
                title={course.title}
                dividerBeforeMeta
                badges={[
                  {
                    key: 'progress',
                    label: `${completedLessons}/${totalLessons} lessons complete`,
                    variant: percentage === 100 ? 'success' : 'accent',
                  },
                  { key: 'modules', label: `${moduleCount} modules`, variant: 'surface' },
                  { key: 'topics', label: `${topicCount} topics`, variant: 'surface' },
                ]}
                summary={progressSummary}
                description={courseDescription}
              />
            </motion.div>
          </div>

          <motion.aside
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.12, ease: EASE }}
            className="learner-double:pt-10"
          >
            <div className="learner-double:sticky learner-double:top-[var(--sticky-top-offset)]">
              <CourseActionPanel
                eyebrow="Continue Learning"
                title={`${percentage}% complete`}
                description={
                  nextLessonOrder != null ? `Next: lesson ${nextLessonOrder}` : 'Course completed'
                }
                tone="accent"
                header={
                  <div className="flex items-center gap-3.5">
                    <ProgressRing value={percentage} color="var(--color-accent)" />
                    <div>
                      <p className="text-panel-title">{percentage}% complete</p>
                      <p className="text-meta" style={{ marginTop: 3 }}>
                        {nextLessonOrder != null
                          ? `Next: lesson ${nextLessonOrder}`
                          : 'Course completed'}
                      </p>
                    </div>
                  </div>
                }
              >
                <div className="flex flex-col gap-2.5">
                  {nextLesson ? (
                    <Button
                      variant="primary"
                      size="xl"
                      icon={<Play size={19} />}
                      onPress={handleContinue}
                      loading={continueLoading}
                      style={{
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      {completedLessons === 0 ? 'Start' : 'Resume'}
                    </Button>
                  ) : (
                    <p className="text-meta-muted">
                      You have completed all available lessons in this course.
                    </p>
                  )}

                  {completedLessonIds.length > 0 && (
                    <Button
                      size="md"
                      variant="secondary"
                      icon={<RotateCcw size={14} />}
                      onPress={handleRevise}
                      loading={createReviseSession.isPending}
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      Revise this course
                    </Button>
                  )}
                </div>
              </CourseActionPanel>
            </div>
          </motion.aside>
        </div>
      </section>

      <motion.section
        className="relative"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.16, ease: EASE }}
      >
        <CourseCurriculumAccordion
          modules={modules}
          progress={progress}
          selectedModuleId={selectedModuleId}
          onSelectModule={setSelectedModuleId}
          activeModuleId={activeModuleId}
          activeTopicId={activeTopicId}
          onOpenLesson={handleOpenLesson}
        />
      </motion.section>
    </PageContainer>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function CoursePageSkeleton() {
  return (
    <PageContainer className="pb-24 pt-10">
      <div
        className="h-4 w-12 animate-pulse rounded"
        style={{ background: 'var(--color-surface-muted)' }}
      />
      <div
        className="mt-6 h-36 w-full animate-pulse rounded-[20px]"
        style={{ background: 'var(--color-surface-muted)' }}
      />
      <div className="mt-8 flex gap-3">
        <div
          className="h-10 w-48 animate-pulse rounded-xl"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="h-10 w-36 animate-pulse rounded-xl"
          style={{ background: 'var(--color-surface-muted)' }}
        />
      </div>
      <div className="mt-4 space-y-4">
        {[1, 2, 3].map((idx) => (
          <div
            key={idx}
            className="h-20 animate-pulse rounded-2xl"
            style={{ background: 'var(--color-surface-muted)' }}
          />
        ))}
      </div>
    </PageContainer>
  );
}

interface LessonLocation {
  moduleId: string;
  topicId: string;
  order: number;
}

function findNextLesson(progress: CourseProgress | undefined) {
  if (!progress) return null;
  return (
    progress.lessons.find((lesson) => lesson.status === 'in-progress') ??
    progress.lessons.find((lesson) => lesson.status === 'failed') ??
    progress.lessons.find((lesson) => lesson.status === 'available') ??
    null
  );
}

function buildHeroSubtitle({
  totalLessons,
  completedLessons,
  percentage,
  nextLessonOrder,
  timeRemaining,
}: {
  totalLessons: number;
  completedLessons: number;
  percentage: number;
  nextLessonOrder: number | undefined;
  timeRemaining: string | null;
}): string {
  if (totalLessons === 0) return 'No published lessons yet.';

  if (nextLessonOrder != null && percentage < 100) {
    return `Lesson ${nextLessonOrder} of ${totalLessons}${timeRemaining ? ` · ~${timeRemaining} remaining` : ''}`;
  }

  if (percentage >= 100) return `All ${totalLessons} lessons complete.`;

  return `${completedLessons} of ${totalLessons} lessons complete${timeRemaining ? ` · ~${timeRemaining} remaining` : ''}`;
}
